import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import useSurvey from '../../hooks/useSurvey';
import SurveyIntro from './SurveyIntro';
import StudentIDInput from './StudentIDInput';
import SurveyStep from './SurveyStep';
import ClusterSelection from './ClusterSelection';
import SurveyResults from './SurveyResults';
import ChatInterface from './ChatInterface';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import Footer from '../common/Footer';

const Survey = ({ districtProp = null, embedded = false, customTitle = null, customMessage = null, footerConfig = null }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [district, setDistrict] = useState(districtProp);
  const [showSelector, setShowSelector] = useState(false);

  // Check for district parameter on mount
  useEffect(() => {
    // If district is passed as prop (embedded mode), use it
    if (districtProp) {
      setDistrict(districtProp);
      setShowSelector(false);
      return;
    }

    // Otherwise check URL params
    const districtParam = searchParams.get('district');
    if (districtParam) {
      setDistrict(districtParam);
      setShowSelector(false);
    } else {
      setShowSelector(true);
    }
  }, [searchParams, districtProp, embedded]);

  // Show friendly message if no district parameter in URL
  if (showSelector) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-blue-100 rounded-full p-4">
              <svg className="h-12 w-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            Welcome to the Career Interest Survey!
          </h1>
          <p className="text-gray-600 mb-4">
            To get started, please use the personalized link provided by your school district. Your school counselor or teacher should have shared this link with you.
          </p>
          <p className="text-sm text-gray-500">
            If you haven't received a link yet, reach out to your school counselor — they'll get you set up.
          </p>
        </div>
      </div>
    );
  }

  // Wait for district to be set
  if (!district) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  return <SurveyWithDistrict district={district} customTitle={customTitle} customMessage={customMessage} footerConfig={footerConfig} />;
};

// Separate component that receives district prop
const SurveyWithDistrict = ({ district, customTitle, customMessage, footerConfig }) => {
  const {
    // Survey flow state
    surveyStage,
    currentStep,
    totalSteps,
    studentID,

    // Survey data
    responses,
    questions,
    getCurrentStepQuestions,

    // AI Results
    llmRecommendations,
    aiSummary,
    selectedClusters,

    // UI state
    loading,
    error,
    validatingStudentID,
    validationInfo,

    // Actions
    startSurvey,
    setStudentIDAndProceed,
    toggleResponse,
    nextStep,
    prevStep,
    analyzeSurvey,
    finalizeSurvey,
    skipToChat,
    proceedToSurvey,
    proceedToChat,
    backToResults,
    chatSession,
    updateChatSession
  } = useSurvey(district); // Pass district to hook

  // Handle skipping directly to chat for returning students
  const handleSkipToChat = (id, clusters) => {
    if (skipToChat) {
      skipToChat(id, clusters);
    }
  };

  // Initial loading state
  if (loading && surveyStage === 'intro') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-1 py-8 px-4">
        <div className="container mx-auto">
          {/* Survey Intro */}
          {surveyStage === 'intro' && (
            <SurveyIntro
              onStart={startSurvey}
              customTitle={customTitle}
              customMessage={customMessage}
            />
          )}

          {/* Student ID Input */}
          {surveyStage === 'studentID' && (
            <StudentIDInput
              onSubmit={setStudentIDAndProceed}
              validatingStudentID={validatingStudentID}
              error={error}
              validationInfo={validationInfo}
              onSkipToChat={handleSkipToChat}
              onProceedToSurvey={proceedToSurvey}
            />
          )}

          {/* Survey Steps */}
          {surveyStage === 'survey' && (
            <>
              {/* Welcome Message */}
              {validationInfo?.studentInfo && (
                <div className="max-w-3xl mx-auto mb-6">
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-l-4 border-blue-500 rounded-lg p-4 shadow-sm">
                    <h2 className="text-xl font-semibold text-gray-800">
                      Welcome, {validationInfo.studentInfo.firstName} {validationInfo.studentInfo.lastName}!
                    </h2>
                    {validationInfo.studentInfo.gradeLevel && (
                      <p className="text-sm text-gray-600 mt-1">
                        Grade {validationInfo.studentInfo.gradeLevel}
                        {validationInfo.studentInfo.campus && ` • ${validationInfo.studentInfo.campus}`}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {error && (
                <div className="max-w-3xl mx-auto mb-4">
                  <ErrorMessage message={error} />
                </div>
              )}
              <SurveyStep
                stepData={getCurrentStepQuestions()}
                currentStep={currentStep}
                totalSteps={totalSteps}
                responses={responses}
                onToggleResponse={toggleResponse}
                onNext={nextStep}
                onBack={prevStep}
                onAnalyze={analyzeSurvey}
                loading={loading}
              />
            </>
          )}

          {/* Cluster Selection */}
          {surveyStage === 'clusterSelection' && (
            <>
              {error && (
                <div className="max-w-4xl mx-auto mb-4">
                  <ErrorMessage message={error} />
                </div>
              )}
              <ClusterSelection
                llmRecommendations={llmRecommendations}
                summary={aiSummary}
                onSubmit={finalizeSurvey}
                loading={loading}
              />
            </>
          )}

          {/* Results */}
          {surveyStage === 'results' && (
            <SurveyResults
              studentID={studentID}
              selectedClusters={selectedClusters}
              district={district}
              onProceedToChat={district ? proceedToChat : null}
            />
          )}

          {/* Pathways Advisor Chat */}
          {surveyStage === 'chat' && (
            <ChatInterface
              studentID={studentID}
              selectedClusters={selectedClusters}
              district={district}
              inline={true}
              onBack={backToResults}
              chatSession={chatSession}
              onUpdateChatSession={updateChatSession}
            />
          )}
        </div>
      </div>

      {/* Footer */}
      <Footer config={footerConfig} />
    </div>
  );
};

export default Survey;
